import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { RevealLines } from "@/components/Reveal";

const HERO_BG = "https://images.pexels.com/photos/18051278/pexels-photo-18051278.jpeg";

export default function Home() {
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-primary">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={HERO_BG} alt="Ocean waves from above" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-primary" aria-hidden />

        <motion.div
          style={{ y: textY }}
          className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 h-full flex flex-col justify-end pb-20 md:pb-28"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="label text-primary-foreground/70 mb-6 md:mb-10"
          >
            / Premium Research Grade Compounds
          </motion.p>

          <h1 className="font-display font-light text-primary-foreground text-[15vw] md:text-[9vw] leading-[0.85] tracking-tight">
            <RevealLines lines={["Chemistry", "that makes"]} delay={0.35} />
            <span className="block text-primary-foreground italic">
              <RevealLines lines={["Waves."]} delay={0.62} />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-10 md:mt-14 flex flex-col sm:flex-row sm:items-end gap-8 justify-between"
          >
            <p className="max-w-md text-sm text-primary-foreground/80 leading-relaxed">
              Reliable resources for molecular studies, metabolic research, and regenerative science — ensuring every experiment is accurate, reproducible, and positioned for meaningful breakthroughs.
            </p>
            <Link
              to="/shop"
              className="group inline-flex items-center gap-3 bg-background text-foreground px-8 py-4 hover:bg-accent hover:text-accent-foreground transition-colors duration-400"
              data-testid="hero-shop-cta"
            >
              <span className="label">Shop the collection</span>
              <ArrowUpRight size={18} strokeWidth={1.5} className="group-hover:rotate-45 transition-transform duration-400" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 right-6 md:right-12 z-10 text-primary-foreground/60"
        >
          <ArrowDown size={20} strokeWidth={1.5} className="animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border bg-background py-5" data-testid="marquee">
        <Marquee speed={40} gradient={false} autoFill>
          {["Third-Party Tested", "High Purity", "Clinically Dosed", "High Quality", "Certificate of Analysis", "Research Use Only — Not For Human Consumption"].map((t, i) => (
            <span key={i} className="label text-foreground/70 mx-8 flex items-center gap-8">
              {t} <span className="text-accent">✦</span>
            </span>
          ))}
        </Marquee>
      </section>
    </main>
  );
}
